import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Users, Calendar, Clock, Plus, X, Check, Edit2, Trash2,
  ChevronLeft, ChevronRight, AlertTriangle, DollarSign, Coffee,
  UserPlus, Save, Eye, RefreshCw, Scale, ArrowRightLeft, CheckCircle2,
  Download, FileSpreadsheet, FileText, User, Repeat, Search, Filter,
  TrendingUp, TrendingDown, Activity, MoreVertical, ArrowUpDown,
  Bell, Mail, Phone, Briefcase, Euro, CalendarDays, ChevronDown,
  BarChart3, PieChart, Clock3, UserCheck, AlertCircle, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function HRAgendaPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  
  // Données
  const [employees, setEmployees] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [overtime, setOvertime] = useState([]);
  const [recovery, setRecovery] = useState([]);
  const [alerts, setAlerts] = useState({ unpaid_overtime: [], pending_recovery: [], unjustified_absences: [], total_alerts: 0 });
  const [employeeSummaries, setEmployeeSummaries] = useState({});
  
  // Calendrier
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  
  // Fiche employé
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null);
  
  // Modals
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventType, setEventType] = useState('absence');
  
  // Recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Forms
  const [eventForm, setEventForm] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    hours: '',
    reason: '',
    description: ''
  });
  
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    position: '',
    hourly_rate: '',
    phone: '',
    email: ''
  });

  const token = localStorage.getItem('token');

  // ============= DATA FETCHING =============
  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchMonthData();
  }, [currentDate, selectedEmployee]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchEmployees(), fetchAlerts()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/api/hr/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activeEmployees = res.data.filter(e => e.is_active !== false);
      setEmployees(activeEmployees);
      
      // Fetch summaries for all employees
      const summaries = {};
      for (const emp of activeEmployees) {
        try {
          const sumRes = await axios.get(`${API}/api/hr/summary/${emp.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          summaries[emp.id] = sumRes.data;
        } catch (e) {
          console.error(`Error fetching summary for ${emp.id}`);
        }
      }
      setEmployeeSummaries(summaries);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchMonthData = async () => {
    const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    const params = { month };
    if (selectedEmployee !== 'all') params.employee_id = selectedEmployee;
    
    try {
      const [absRes, overRes, recRes] = await Promise.all([
        axios.get(`${API}/api/hr/absences`, { headers: { Authorization: `Bearer ${token}` }, params }),
        axios.get(`${API}/api/hr/overtime`, { headers: { Authorization: `Bearer ${token}` }, params }),
        axios.get(`${API}/api/hr/recovery`, { headers: { Authorization: `Bearer ${token}` }, params })
      ]);
      setAbsences(absRes.data);
      setOvertime(overRes.data);
      setRecovery(recRes.data);
    } catch (error) {
      console.error('Error fetching month data:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/api/hr/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(res.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  // ============= COMPUTED VALUES =============
  const stats = useMemo(() => {
    const totalOvertime = overtime.reduce((sum, o) => sum + (o.hours || 0), 0);
    const unpaidOvertime = overtime.filter(o => !o.is_paid).reduce((sum, o) => sum + (o.hours || 0), 0);
    const totalRecovery = recovery.reduce((sum, r) => sum + (r.hours || 0), 0);
    const pendingRecovery = recovery.filter(r => !r.is_taken).reduce((sum, r) => sum + (r.hours || 0), 0);
    const totalAbsences = absences.length;
    const unjustifiedAbsences = absences.filter(a => !a.is_justified).length;
    const unpaidAmount = overtime.filter(o => !o.is_paid).reduce((sum, o) => sum + (o.amount || 0), 0);
    
    return {
      totalOvertime,
      unpaidOvertime,
      totalRecovery,
      pendingRecovery,
      totalAbsences,
      unjustifiedAbsences,
      unpaidAmount,
      balance: totalOvertime - totalRecovery
    };
  }, [overtime, recovery, absences]);

  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const matchesSearch = emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           emp.position?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [employees, searchTerm]);

  // ============= EMPLOYEE ACTIONS =============
  const saveEmployee = async () => {
    if (!employeeForm.name) {
      toast.error('Le nom est requis');
      return;
    }
    try {
      if (editingEmployee) {
        await axios.put(`${API}/api/hr/employees/${editingEmployee.id}`, {
          ...employeeForm,
          hourly_rate: parseFloat(employeeForm.hourly_rate) || 0
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Employé modifié avec succès');
      } else {
        await axios.post(`${API}/api/hr/employees`, {
          ...employeeForm,
          hourly_rate: parseFloat(employeeForm.hourly_rate) || 0
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Employé ajouté avec succès');
      }
      setShowEmployeeModal(false);
      setEditingEmployee(null);
      setEmployeeForm({ name: '', position: '', hourly_rate: '', phone: '', email: '' });
      fetchEmployees();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) return;
    try {
      await axios.delete(`${API}/api/hr/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employé supprimé');
      fetchEmployees();
      if (selectedEmployeeDetails?.id === id) setSelectedEmployeeDetails(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.name,
      position: emp.position || '',
      hourly_rate: emp.hourly_rate?.toString() || '',
      phone: emp.phone || '',
      email: emp.email || ''
    });
    setShowEmployeeModal(true);
  };

  const openEmployeeCard = (emp) => {
    setSelectedEmployeeDetails(emp);
    setActiveView('employee-detail');
  };

  // ============= EVENT ACTIONS =============
  const openCreateEvent = (type, empId = null) => {
    setEventType(type);
    setEventForm({
      employee_id: empId || selectedEmployeeDetails?.id || '',
      date: new Date().toISOString().split('T')[0],
      hours: '',
      reason: '',
      description: ''
    });
    setShowEventModal(true);
  };

  const saveEvent = async () => {
    if (!eventForm.employee_id || !eventForm.date) {
      toast.error('Employé et date requis');
      return;
    }
    
    try {
      const endpoint = eventType === 'absence' ? 'absences' : eventType === 'overtime' ? 'overtime' : 'recovery';
      const data = {
        employee_id: eventForm.employee_id,
        date: eventForm.date,
        hours: parseFloat(eventForm.hours) || 0
      };
      
      if (eventType === 'absence') {
        data.reason = eventForm.reason;
        data.is_justified = false;
      } else {
        data.description = eventForm.description;
        if (eventType === 'overtime') data.is_paid = false;
        if (eventType === 'recovery') data.is_taken = false;
      }
      
      await axios.post(`${API}/api/hr/${endpoint}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Événement enregistré');
      setShowEventModal(false);
      fetchMonthData();
      fetchAlerts();
      fetchEmployees();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const toggleStatus = async (type, item, field) => {
    const endpoint = type === 'overtime' ? 'overtime' : type === 'recovery' ? 'recovery' : 'absences';
    try {
      await axios.put(`${API}/api/hr/${endpoint}/${item.id}`, {
        [field]: !item[field]
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Statut mis à jour');
      fetchMonthData();
      fetchAlerts();
      fetchEmployees();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteEvent = async (type, id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    const endpoint = type === 'overtime' ? 'overtime' : type === 'recovery' ? 'recovery' : 'absences';
    try {
      await axios.delete(`${API}/api/hr/${endpoint}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Événement supprimé');
      fetchMonthData();
      fetchAlerts();
      fetchEmployees();
      setShowEventDetailsModal(false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEventDetails = (event, type) => {
    setSelectedEvent({ ...event, type });
    setShowEventDetailsModal(true);
  };

  const convertEvent = async (fromType, toType) => {
    if (!selectedEvent) return;
    
    try {
      const endpoint = toType === 'absence' ? 'absences' : toType === 'overtime' ? 'overtime' : 'recovery';
      const data = {
        employee_id: selectedEvent.employee_id,
        date: selectedEvent.date,
        hours: selectedEvent.hours || 0
      };
      
      if (toType === 'absence') {
        data.reason = `Converti depuis ${fromType === 'overtime' ? 'heures sup' : 'récupération'}`;
        data.is_justified = true;
      } else if (toType === 'overtime') {
        data.description = `Converti depuis ${fromType === 'absence' ? 'absence' : 'récupération'}`;
        data.is_paid = false;
      } else {
        data.description = `Converti depuis ${fromType === 'absence' ? 'absence' : 'heures sup'}`;
        data.is_taken = false;
      }
      
      await axios.post(`${API}/api/hr/${endpoint}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const oldEndpoint = fromType === 'overtime' ? 'overtime' : fromType === 'recovery' ? 'recovery' : 'absences';
      await axios.delete(`${API}/api/hr/${oldEndpoint}/${selectedEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Converti en ${toType === 'absence' ? 'absence' : toType === 'overtime' ? 'heures sup' : 'récupération'}`);
      setShowConvertModal(false);
      setShowEventDetailsModal(false);
      fetchMonthData();
      fetchAlerts();
      fetchEmployees();
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  // ============= CALENDAR HELPERS =============
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let i = 0; i < (firstDay.getDay() || 7) - 1; i++) {
      days.push(null);
    }
    
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return { absences: [], overtime: [], recovery: [] };
    const dateStr = date.toISOString().split('T')[0];
    return {
      absences: absences.filter(a => a.date === dateStr),
      overtime: overtime.filter(o => o.date === dateStr),
      recovery: recovery.filter(r => r.date === dateStr)
    };
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // ============= RENDER =============
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Chargement du module RH...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/80 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Button 
                onClick={() => navigate('/')} 
                variant="ghost" 
                size="sm" 
                className="text-slate-400 hover:text-white hover:bg-slate-700/50"
              >
                <Home className="w-4 h-4 mr-2" />
                Accueil
              </Button>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Agenda RH</h1>
                  <p className="text-xs text-slate-400">{employees.length} employé{employees.length > 1 ? 's' : ''} actif{employees.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              
              {alerts.total_alerts > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/30 animate-pulse">
                  <Bell className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-medium text-red-400">
                    {alerts.total_alerts} alerte{alerts.total_alerts > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {/* Quick Actions */}
              <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg p-1">
                <Button
                  onClick={() => openCreateEvent('absence')}
                  size="sm"
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border-0"
                  data-testid="btn-add-absence"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Absence
                </Button>
                <Button
                  onClick={() => openCreateEvent('overtime')}
                  size="sm"
                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border-0"
                  data-testid="btn-add-overtime"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Heures Sup
                </Button>
                <Button
                  onClick={() => openCreateEvent('recovery')}
                  size="sm"
                  className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border-0"
                  data-testid="btn-add-recovery"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Récup
                </Button>
              </div>
              
              {/* Export Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
                    window.open(`${API}/api/hr/export/excel?month=${month}`, '_blank');
                  }}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-export-excel"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-1" />
                  Excel
                </Button>
                <Button
                  onClick={() => {
                    const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
                    window.open(`${API}/api/hr/export/pdf?month=${month}`, '_blank');
                  }}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="btn-export-pdf"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Navigation Tabs */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-xl p-1.5">
            {[
              { id: 'dashboard', icon: BarChart3, label: 'Tableau de bord' },
              { id: 'employees', icon: Users, label: 'Employés' },
              { id: 'calendar', icon: Calendar, label: 'Calendrier' },
              { id: 'events', icon: Activity, label: 'Événements' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveView(tab.id); setSelectedEmployeeDetails(null); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                  activeView === tab.id 
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          
          {activeView === 'calendar' && (
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="select-employee-filter"
            >
              <option value="all">Tous les employés</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Dashboard View */}
        {activeView === 'dashboard' && (
          <div className="space-y-6" data-testid="dashboard-view">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-2xl border border-amber-500/30 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-400" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">+{stats.totalOvertime}h</p>
                <p className="text-sm text-amber-300/70">Heures supplémentaires</p>
                {stats.unpaidOvertime > 0 && (
                  <p className="text-xs text-red-400 mt-2 animate-pulse">{stats.unpaidOvertime}h non payées</p>
                )}
              </div>
              
              <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-2xl border border-emerald-500/30 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Coffee className="w-6 h-6 text-emerald-400" />
                  </div>
                  <TrendingDown className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">-{stats.totalRecovery}h</p>
                <p className="text-sm text-emerald-300/70">Récupérations</p>
                {stats.pendingRecovery > 0 && (
                  <p className="text-xs text-blue-400 mt-2 animate-pulse">{stats.pendingRecovery}h à prendre</p>
                )}
              </div>
              
              <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-2xl border border-red-500/30 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <span className="text-2xl font-bold text-red-400">{stats.totalAbsences}</span>
                </div>
                <p className="text-3xl font-bold text-white mb-1">{stats.unjustifiedAbsences}</p>
                <p className="text-sm text-red-300/70">Absences non justifiées</p>
                <p className="text-xs text-slate-400 mt-2">{stats.totalAbsences} total ce mois</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-2xl border border-purple-500/30 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Euro className="w-6 h-6 text-purple-400" />
                  </div>
                  <DollarSign className="w-5 h-5 text-purple-400" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">{stats.unpaidAmount.toFixed(0)}€</p>
                <p className="text-sm text-purple-300/70">Montant à payer</p>
                <p className="text-xs text-slate-400 mt-2">Solde: {stats.balance > 0 ? '+' : ''}{stats.balance}h</p>
              </div>
            </div>

            {/* Alerts Section */}
            {alerts.total_alerts > 0 && (
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  Alertes en attente
                </h2>
                <div className="grid grid-cols-3 gap-4">
                  {alerts.unpaid_overtime.length > 0 && (
                    <div className="bg-amber-500/10 rounded-xl border border-amber-500/30 p-4">
                      <h3 className="text-amber-400 font-medium mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Heures sup non payées ({alerts.unpaid_overtime.length})
                      </h3>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {alerts.unpaid_overtime.slice(0, 5).map(ot => (
                          <div 
                            key={ot.id} 
                            onClick={() => openEventDetails(ot, 'overtime')}
                            className="flex items-center justify-between p-2 rounded-lg bg-slate-900/50 cursor-pointer hover:bg-slate-900/80"
                          >
                            <span className="text-white text-sm">{ot.employee_name}</span>
                            <span className="text-amber-400 font-medium">+{ot.hours}h</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {alerts.pending_recovery.length > 0 && (
                    <div className="bg-emerald-500/10 rounded-xl border border-emerald-500/30 p-4">
                      <h3 className="text-emerald-400 font-medium mb-3 flex items-center gap-2">
                        <Coffee className="w-4 h-4" />
                        Récup à prendre ({alerts.pending_recovery.length})
                      </h3>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {alerts.pending_recovery.slice(0, 5).map(rec => (
                          <div 
                            key={rec.id}
                            onClick={() => openEventDetails(rec, 'recovery')}
                            className="flex items-center justify-between p-2 rounded-lg bg-slate-900/50 cursor-pointer hover:bg-slate-900/80"
                          >
                            <span className="text-white text-sm">{rec.employee_name}</span>
                            <span className="text-emerald-400 font-medium">-{rec.hours}h</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {alerts.unjustified_absences.length > 0 && (
                    <div className="bg-red-500/10 rounded-xl border border-red-500/30 p-4">
                      <h3 className="text-red-400 font-medium mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Absences non justifiées ({alerts.unjustified_absences.length})
                      </h3>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {alerts.unjustified_absences.slice(0, 5).map(abs => (
                          <div 
                            key={abs.id}
                            onClick={() => openEventDetails(abs, 'absence')}
                            className="flex items-center justify-between p-2 rounded-lg bg-slate-900/50 cursor-pointer hover:bg-slate-900/80"
                          >
                            <span className="text-white text-sm">{abs.employee_name}</span>
                            <span className="text-red-400 text-xs">{abs.date}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Employees Overview */}
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  Aperçu des employés
                </h2>
                <Button 
                  onClick={() => setActiveView('employees')}
                  variant="ghost"
                  size="sm"
                  className="text-blue-400 hover:text-blue-300"
                >
                  Voir tout
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {employees.slice(0, 6).map(emp => {
                  const summary = employeeSummaries[emp.id];
                  const overtimeHours = summary?.overtime?.total_hours || 0;
                  const recoveryHours = summary?.recovery?.total_hours || 0;
                  const balance = overtimeHours - recoveryHours;
                  
                  return (
                    <div 
                      key={emp.id}
                      onClick={() => openEmployeeCard(emp)}
                      className="bg-slate-900/50 rounded-xl p-4 cursor-pointer hover:bg-slate-900/80 transition-all hover:scale-[1.02] border border-transparent hover:border-blue-500/30"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white font-bold">{emp.name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white truncate">{emp.name}</h3>
                          <p className="text-xs text-slate-400 truncate">{emp.position || 'Employé'}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">{emp.hourly_rate || 0}€/h</span>
                        <span className={`font-medium ${balance > 0 ? 'text-amber-400' : balance < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                          {balance > 0 ? '+' : ''}{balance}h
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Employees View */}
        {activeView === 'employees' && (
          <div className="space-y-6" data-testid="employees-view">
            <div className="flex items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un employé..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="search-employees"
                />
              </div>
              <Button
                onClick={() => { setEditingEmployee(null); setEmployeeForm({ name: '', position: '', hourly_rate: '', phone: '', email: '' }); setShowEmployeeModal(true); }}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="btn-add-employee"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Nouvel employé
              </Button>
            </div>

            {filteredEmployees.length === 0 ? (
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">Aucun employé</h3>
                <p className="text-slate-400 mb-6">Commencez par ajouter votre premier employé</p>
                <Button
                  onClick={() => { setEditingEmployee(null); setEmployeeForm({ name: '', position: '', hourly_rate: '', phone: '', email: '' }); setShowEmployeeModal(true); }}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ajouter un employé
                </Button>
              </div>
            ) : (
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700/50">
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Employé</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Poste</th>
                      <th className="text-center px-6 py-4 text-sm font-medium text-slate-400">Taux horaire</th>
                      <th className="text-center px-6 py-4 text-sm font-medium text-slate-400">Heures Sup</th>
                      <th className="text-center px-6 py-4 text-sm font-medium text-slate-400">Récup</th>
                      <th className="text-center px-6 py-4 text-sm font-medium text-slate-400">Solde</th>
                      <th className="text-right px-6 py-4 text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map(emp => {
                      const summary = employeeSummaries[emp.id];
                      const overtimeHours = summary?.overtime?.total_hours || 0;
                      const unpaidHours = summary?.overtime?.unpaid_hours || 0;
                      const recoveryHours = summary?.recovery?.total_hours || 0;
                      const pendingRecovery = summary?.recovery?.pending_hours || 0;
                      const balance = overtimeHours - recoveryHours;
                      
                      return (
                        <tr 
                          key={emp.id} 
                          className="border-b border-slate-700/30 hover:bg-slate-700/20 cursor-pointer transition-colors"
                          onClick={() => openEmployeeCard(emp)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                <span className="text-white font-bold">{emp.name?.charAt(0)}</span>
                              </div>
                              <div>
                                <p className="font-medium text-white">{emp.name}</p>
                                {emp.email && <p className="text-xs text-slate-400">{emp.email}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-300">{emp.position || '-'}</td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-emerald-400 font-medium">{emp.hourly_rate || 0}€/h</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-amber-400 font-medium">+{overtimeHours}h</span>
                            {unpaidHours > 0 && (
                              <span className="ml-2 text-xs text-red-400 animate-pulse">({unpaidHours}h non payées)</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-emerald-400 font-medium">-{recoveryHours}h</span>
                            {pendingRecovery > 0 && (
                              <span className="ml-2 text-xs text-blue-400 animate-pulse">({pendingRecovery}h à prendre)</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`font-bold text-lg ${balance > 0 ? 'text-amber-400' : balance < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                              {balance > 0 ? '+' : ''}{balance}h
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                              <Button
                                onClick={() => openEmployeeCard(emp)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => openEditEmployee(emp)}
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-white hover:bg-slate-700"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => deleteEmployee(emp.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Employee Detail View */}
        {activeView === 'employee-detail' && selectedEmployeeDetails && (
          <div className="space-y-6" data-testid="employee-detail-view">
            {/* Employee Header */}
            <div className="bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl border border-blue-500/30 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button 
                    onClick={() => setActiveView('employees')} 
                    variant="ghost" 
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Retour
                  </Button>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                    <span className="text-white font-bold text-2xl">{selectedEmployeeDetails.name?.charAt(0)}</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedEmployeeDetails.name}</h2>
                    <p className="text-slate-400">{selectedEmployeeDetails.position || 'Employé'}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-emerald-400 font-semibold">{selectedEmployeeDetails.hourly_rate || 0}€/h</span>
                      {selectedEmployeeDetails.phone && (
                        <span className="text-slate-400 text-sm flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedEmployeeDetails.phone}
                        </span>
                      )}
                      {selectedEmployeeDetails.email && (
                        <span className="text-slate-400 text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {selectedEmployeeDetails.email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => openCreateEvent('absence', selectedEmployeeDetails.id)} className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30">
                    <Plus className="w-4 h-4 mr-1" /> Absence
                  </Button>
                  <Button onClick={() => openCreateEvent('overtime', selectedEmployeeDetails.id)} className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30">
                    <Plus className="w-4 h-4 mr-1" /> Heures Sup
                  </Button>
                  <Button onClick={() => openCreateEvent('recovery', selectedEmployeeDetails.id)} className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30">
                    <Plus className="w-4 h-4 mr-1" /> Récup
                  </Button>
                </div>
              </div>
            </div>

            {/* Employee Stats */}
            {(() => {
              const summary = employeeSummaries[selectedEmployeeDetails.id] || {};
              const overtimeHours = summary.overtime?.total_hours || 0;
              const unpaidHours = summary.overtime?.unpaid_hours || 0;
              const unpaidAmount = summary.overtime?.unpaid_amount || 0;
              const recoveryHours = summary.recovery?.total_hours || 0;
              const pendingRecovery = summary.recovery?.pending_hours || 0;
              const absenceCount = summary.absences?.total_count || 0;
              const unjustifiedCount = summary.absences?.unjustified_count || 0;
              const balance = overtimeHours - recoveryHours;
              
              return (
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-amber-500/10 rounded-xl border border-amber-500/30 p-4">
                    <p className="text-xs text-amber-300/70 mb-1">Heures supplémentaires</p>
                    <p className="text-2xl font-bold text-amber-400">+{overtimeHours}h</p>
                    {unpaidHours > 0 && (
                      <p className="text-xs text-red-400 mt-1">{unpaidHours}h non payées ({unpaidAmount.toFixed(0)}€)</p>
                    )}
                  </div>
                  <div className="bg-emerald-500/10 rounded-xl border border-emerald-500/30 p-4">
                    <p className="text-xs text-emerald-300/70 mb-1">Récupérations</p>
                    <p className="text-2xl font-bold text-emerald-400">-{recoveryHours}h</p>
                    {pendingRecovery > 0 && (
                      <p className="text-xs text-blue-400 mt-1">{pendingRecovery}h à prendre</p>
                    )}
                  </div>
                  <div className="bg-red-500/10 rounded-xl border border-red-500/30 p-4">
                    <p className="text-xs text-red-300/70 mb-1">Absences</p>
                    <p className="text-2xl font-bold text-white">{absenceCount}</p>
                    {unjustifiedCount > 0 && (
                      <p className="text-xs text-red-400 mt-1">{unjustifiedCount} non justifiées</p>
                    )}
                  </div>
                  <div className="bg-purple-500/10 rounded-xl border border-purple-500/30 p-4">
                    <p className="text-xs text-purple-300/70 mb-1">Solde global</p>
                    <p className={`text-2xl font-bold ${balance > 0 ? 'text-amber-400' : balance < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                      {balance > 0 ? '+' : ''}{balance}h
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Heures sup - Récup</p>
                  </div>
                </div>
              );
            })()}

            {/* Events Lists */}
            <div className="grid grid-cols-3 gap-4">
              {/* Absences */}
              <div className="bg-slate-800/50 rounded-2xl border border-red-500/20 p-5">
                <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Absences
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {absences.filter(a => a.employee_id === selectedEmployeeDetails.id).length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">Aucune absence</p>
                  ) : (
                    absences.filter(a => a.employee_id === selectedEmployeeDetails.id).map(a => (
                      <div
                        key={a.id}
                        onClick={() => openEventDetails(a, 'absence')}
                        className={`p-3 rounded-xl bg-slate-900/50 cursor-pointer hover:bg-slate-900/80 transition-all ${
                          !a.is_justified ? 'border border-red-500/50 animate-pulse' : 'border border-transparent'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                            <p className="text-sm text-slate-400 truncate max-w-[150px]">{a.reason}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${a.is_justified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                            {a.is_justified ? 'Justifié' : 'Non justifié'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Heures sup */}
              <div className="bg-slate-800/50 rounded-2xl border border-amber-500/20 p-5">
                <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Heures Supplémentaires
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {overtime.filter(o => o.employee_id === selectedEmployeeDetails.id).length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">Aucune heure sup</p>
                  ) : (
                    overtime.filter(o => o.employee_id === selectedEmployeeDetails.id).map(o => (
                      <div
                        key={o.id}
                        onClick={() => openEventDetails(o, 'overtime')}
                        className={`p-3 rounded-xl bg-slate-900/50 cursor-pointer hover:bg-slate-900/80 transition-all ${
                          !o.is_paid && !o.is_validated ? 'border border-amber-500/50 animate-pulse' : 'border border-transparent'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{new Date(o.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                            <p className="text-amber-400 font-semibold">+{o.hours}h</p>
                            {o.amount > 0 && <p className="text-xs text-emerald-400">{o.amount.toFixed(2)}€</p>}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {o.is_validated && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">Validé</span>}
                            <span className={`text-xs px-2 py-0.5 rounded-full ${o.is_paid ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                              {o.is_paid ? 'Payé' : 'À payer'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Récupérations */}
              <div className="bg-slate-800/50 rounded-2xl border border-emerald-500/20 p-5">
                <h3 className="text-lg font-semibold text-emerald-400 mb-4 flex items-center gap-2">
                  <Coffee className="w-5 h-5" />
                  Récupérations
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {recovery.filter(r => r.employee_id === selectedEmployeeDetails.id).length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">Aucune récup</p>
                  ) : (
                    recovery.filter(r => r.employee_id === selectedEmployeeDetails.id).map(r => (
                      <div
                        key={r.id}
                        onClick={() => openEventDetails(r, 'recovery')}
                        className={`p-3 rounded-xl bg-slate-900/50 cursor-pointer hover:bg-slate-900/80 transition-all ${
                          !r.is_taken ? 'border border-emerald-500/50 animate-pulse' : 'border border-transparent'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{new Date(r.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                            <p className="text-emerald-400 font-semibold">-{r.hours}h</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${r.is_taken ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                            {r.is_taken ? 'Prise' : 'À prendre'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calendar View */}
        {activeView === 'calendar' && (
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6" data-testid="calendar-view">
            <div className="flex items-center justify-between mb-6">
              <Button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))} variant="ghost" className="text-white hover:bg-slate-700">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-xl font-bold text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <Button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))} variant="ghost" className="text-white hover:bg-slate-700">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-slate-400 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((date, idx) => {
                const events = getEventsForDate(date);
                const isToday = date && date.toDateString() === new Date().toDateString();
                const hasEvents = events.absences.length + events.overtime.length + events.recovery.length > 0;
                
                return (
                  <div
                    key={idx}
                    className={`min-h-28 p-2 rounded-xl transition-all ${
                      date ? 'bg-slate-900/30 hover:bg-slate-900/50' : ''
                    } ${isToday ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-slate-800' : ''}`}
                  >
                    {date && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-400' : 'text-slate-400'}`}>
                          {date.getDate()}
                        </div>
                        <div className="space-y-1">
                          {events.absences.slice(0, 2).map(a => (
                            <div
                              key={a.id}
                              onClick={() => openEventDetails(a, 'absence')}
                              className={`text-xs px-1.5 py-0.5 rounded bg-red-500/30 text-red-300 truncate cursor-pointer hover:bg-red-500/50 ${!a.is_justified ? 'animate-pulse' : ''}`}
                            >
                              {a.employee_name?.split(' ')[0]}
                            </div>
                          ))}
                          {events.overtime.slice(0, 2).map(o => (
                            <div
                              key={o.id}
                              onClick={() => openEventDetails(o, 'overtime')}
                              className={`text-xs px-1.5 py-0.5 rounded bg-amber-500/30 text-amber-300 truncate cursor-pointer hover:bg-amber-500/50 ${!o.is_paid ? 'animate-pulse' : ''}`}
                            >
                              {o.employee_name?.split(' ')[0]} +{o.hours}h
                            </div>
                          ))}
                          {events.recovery.slice(0, 2).map(r => (
                            <div
                              key={r.id}
                              onClick={() => openEventDetails(r, 'recovery')}
                              className={`text-xs px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-300 truncate cursor-pointer hover:bg-emerald-500/50 ${!r.is_taken ? 'animate-pulse' : ''}`}
                            >
                              {r.employee_name?.split(' ')[0]} -{r.hours}h
                            </div>
                          ))}
                          {(events.absences.length + events.overtime.length + events.recovery.length) > 4 && (
                            <div className="text-xs text-slate-500 text-center">
                              +{(events.absences.length + events.overtime.length + events.recovery.length) - 4} autres
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="flex items-center gap-6 mt-6 pt-4 border-t border-slate-700/50">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-500/50"></div>
                <span className="text-sm text-slate-400">Absence</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-amber-500/50"></div>
                <span className="text-sm text-slate-400">Heures sup</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-emerald-500/50"></div>
                <span className="text-sm text-slate-400">Récupération</span>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <div className="w-3 h-3 rounded bg-blue-500 animate-pulse"></div>
                <span className="text-sm text-slate-400">Clignote = non traité</span>
              </div>
            </div>
          </div>
        )}

        {/* Events View */}
        {activeView === 'events' && (
          <div className="space-y-6" data-testid="events-view">
            <div className="flex items-center gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="done">Traités</option>
              </select>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {/* Absences List */}
              <div className="bg-slate-800/50 rounded-2xl border border-red-500/20 p-5">
                <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Absences ({absences.filter(a => statusFilter === 'all' || (statusFilter === 'pending' && !a.is_justified) || (statusFilter === 'done' && a.is_justified)).length})
                  </span>
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {absences.filter(a => statusFilter === 'all' || (statusFilter === 'pending' && !a.is_justified) || (statusFilter === 'done' && a.is_justified)).map(a => (
                    <div
                      key={a.id}
                      onClick={() => openEventDetails(a, 'absence')}
                      className={`p-3 rounded-xl bg-slate-900/50 cursor-pointer hover:bg-slate-900/80 ${!a.is_justified ? 'border border-red-500/50 animate-pulse' : 'border border-transparent'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-white font-medium">{a.employee_name}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${a.is_justified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                          {a.is_justified ? 'Justifié' : 'Non justifié'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">{new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                      <p className="text-xs text-slate-500 truncate mt-1">{a.reason}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Overtime List */}
              <div className="bg-slate-800/50 rounded-2xl border border-amber-500/20 p-5">
                <h3 className="text-lg font-semibold text-amber-400 mb-4 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Heures Sup ({overtime.filter(o => statusFilter === 'all' || (statusFilter === 'pending' && !o.is_paid) || (statusFilter === 'done' && o.is_paid)).length})
                  </span>
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {overtime.filter(o => statusFilter === 'all' || (statusFilter === 'pending' && !o.is_paid) || (statusFilter === 'done' && o.is_paid)).map(o => (
                    <div
                      key={o.id}
                      onClick={() => openEventDetails(o, 'overtime')}
                      className={`p-3 rounded-xl bg-slate-900/50 cursor-pointer hover:bg-slate-900/80 ${!o.is_paid && !o.is_validated ? 'border border-amber-500/50 animate-pulse' : 'border border-transparent'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-medium">{o.employee_name}</p>
                          <p className="text-amber-400 font-bold">+{o.hours}h</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {o.is_validated && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">Validé</span>}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${o.is_paid ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                            {o.is_paid ? 'Payé' : 'À payer'}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-400">{new Date(o.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                      {o.amount > 0 && <p className="text-xs text-emerald-400 mt-1">{o.amount.toFixed(2)}€</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Recovery List */}
              <div className="bg-slate-800/50 rounded-2xl border border-emerald-500/20 p-5">
                <h3 className="text-lg font-semibold text-emerald-400 mb-4 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Coffee className="w-5 h-5" />
                    Récupérations ({recovery.filter(r => statusFilter === 'all' || (statusFilter === 'pending' && !r.is_taken) || (statusFilter === 'done' && r.is_taken)).length})
                  </span>
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {recovery.filter(r => statusFilter === 'all' || (statusFilter === 'pending' && !r.is_taken) || (statusFilter === 'done' && r.is_taken)).map(r => (
                    <div
                      key={r.id}
                      onClick={() => openEventDetails(r, 'recovery')}
                      className={`p-3 rounded-xl bg-slate-900/50 cursor-pointer hover:bg-slate-900/80 ${!r.is_taken ? 'border border-emerald-500/50 animate-pulse' : 'border border-transparent'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-medium">{r.employee_name}</p>
                          <p className="text-emerald-400 font-bold">-{r.hours}h</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${r.is_taken ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                          {r.is_taken ? 'Prise' : 'À prendre'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">{new Date(r.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal: Create/Edit Employee */}
      {showEmployeeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <User className="w-5 h-5 text-blue-400" />
                {editingEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}
              </h2>
              <Button onClick={() => setShowEmployeeModal(false)} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Nom complet *</label>
                <Input 
                  value={employeeForm.name} 
                  onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})} 
                  className="bg-slate-900 border-slate-700 text-white" 
                  placeholder="Jean Dupont"
                  data-testid="input-employee-name"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Poste</label>
                <Input 
                  value={employeeForm.position} 
                  onChange={(e) => setEmployeeForm({...employeeForm, position: e.target.value})} 
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="Technicien"
                  data-testid="input-employee-position"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Taux horaire (€/h)</label>
                <Input 
                  type="number" 
                  value={employeeForm.hourly_rate} 
                  onChange={(e) => setEmployeeForm({...employeeForm, hourly_rate: e.target.value})} 
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="15"
                  data-testid="input-employee-rate"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Téléphone</label>
                  <Input 
                    value={employeeForm.phone} 
                    onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})} 
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder="06..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Email</label>
                  <Input 
                    type="email"
                    value={employeeForm.email} 
                    onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})} 
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder="email@exemple.com"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowEmployeeModal(false)} variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700">
                Annuler
              </Button>
              <Button onClick={saveEmployee} className="flex-1 bg-blue-500 hover:bg-blue-600" data-testid="btn-save-employee">
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create Event */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {eventType === 'absence' && <><AlertTriangle className="w-5 h-5 text-red-400" /> Nouvelle absence</>}
                {eventType === 'overtime' && <><Clock className="w-5 h-5 text-amber-400" /> Heures supplémentaires</>}
                {eventType === 'recovery' && <><Coffee className="w-5 h-5 text-emerald-400" /> Récupération</>}
              </h2>
              <Button onClick={() => setShowEventModal(false)} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Employé *</label>
                <select
                  value={eventForm.employee_id}
                  onChange={(e) => setEventForm({...eventForm, employee_id: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-white"
                  data-testid="select-event-employee"
                >
                  <option value="">Sélectionner un employé</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Date *</label>
                <Input 
                  type="date" 
                  value={eventForm.date} 
                  onChange={(e) => setEventForm({...eventForm, date: e.target.value})} 
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="input-event-date"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Nombre d'heures</label>
                <Input 
                  type="number" 
                  step="0.5" 
                  value={eventForm.hours} 
                  onChange={(e) => setEventForm({...eventForm, hours: e.target.value})} 
                  className="bg-slate-900 border-slate-700 text-white" 
                  placeholder="Ex: 4"
                  data-testid="input-event-hours"
                />
              </div>
              
              {eventType === 'absence' ? (
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Motif *</label>
                  <Input 
                    value={eventForm.reason} 
                    onChange={(e) => setEventForm({...eventForm, reason: e.target.value})} 
                    className="bg-slate-900 border-slate-700 text-white" 
                    placeholder="Maladie, congé, RTT..."
                    data-testid="input-event-reason"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Description</label>
                  <Input 
                    value={eventForm.description} 
                    onChange={(e) => setEventForm({...eventForm, description: e.target.value})} 
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder="Description optionnelle..."
                    data-testid="input-event-description"
                  />
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowEventModal(false)} variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700">
                Annuler
              </Button>
              <Button 
                onClick={saveEvent} 
                className={`flex-1 ${
                  eventType === 'absence' ? 'bg-red-500 hover:bg-red-600' : 
                  eventType === 'overtime' ? 'bg-amber-500 hover:bg-amber-600' : 
                  'bg-emerald-500 hover:bg-emerald-600'
                }`}
                data-testid="btn-save-event"
              >
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Event Details */}
      {showEventDetailsModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-slate-800 rounded-2xl border max-w-lg w-full p-6 shadow-2xl ${
            selectedEvent.type === 'absence' ? 'border-red-500/50' :
            selectedEvent.type === 'overtime' ? 'border-amber-500/50' : 'border-emerald-500/50'
          }`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {selectedEvent.type === 'absence' && <AlertTriangle className="w-5 h-5 text-red-400" />}
                {selectedEvent.type === 'overtime' && <Clock className="w-5 h-5 text-amber-400" />}
                {selectedEvent.type === 'recovery' && <Coffee className="w-5 h-5 text-emerald-400" />}
                Détails de l'événement
              </h2>
              <Button onClick={() => setShowEventDetailsModal(false)} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Employé</p>
                  <p className="text-white font-medium">{selectedEvent.employee_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Date</p>
                  <p className="text-white font-medium">{new Date(selectedEvent.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>
              
              {selectedEvent.hours && (
                <div>
                  <p className="text-sm text-slate-400">Durée</p>
                  <p className={`text-xl font-bold ${
                    selectedEvent.type === 'absence' ? 'text-red-400' : 
                    selectedEvent.type === 'overtime' ? 'text-amber-400' : 'text-emerald-400'
                  }`}>
                    {selectedEvent.type === 'overtime' ? '+' : selectedEvent.type === 'recovery' ? '-' : ''}{selectedEvent.hours} heures
                  </p>
                </div>
              )}
              
              {selectedEvent.amount > 0 && (
                <div>
                  <p className="text-sm text-slate-400">Montant</p>
                  <p className="text-emerald-400 font-bold text-lg">{selectedEvent.amount.toFixed(2)} €</p>
                </div>
              )}
              
              {selectedEvent.reason && (
                <div>
                  <p className="text-sm text-slate-400">Motif</p>
                  <p className="text-white">{selectedEvent.reason}</p>
                </div>
              )}
              
              {selectedEvent.description && (
                <div>
                  <p className="text-sm text-slate-400">Description</p>
                  <p className="text-white">{selectedEvent.description}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-slate-400 mb-2">Statut</p>
                <div className="flex gap-2 flex-wrap">
                  {selectedEvent.type === 'absence' && (
                    <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${selectedEvent.is_justified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {selectedEvent.is_justified ? 'Justifié' : 'Non justifié'}
                    </span>
                  )}
                  {selectedEvent.type === 'overtime' && (
                    <>
                      {selectedEvent.is_validated && <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-purple-500/20 text-purple-400">Validé</span>}
                      <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${selectedEvent.is_paid ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                        {selectedEvent.is_paid ? 'Payé' : 'Non payé'}
                      </span>
                    </>
                  )}
                  {selectedEvent.type === 'recovery' && (
                    <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${selectedEvent.is_taken ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                      {selectedEvent.is_taken ? 'Prise' : 'Non prise'}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="border-t border-slate-700 mt-6 pt-4">
              <p className="text-sm text-slate-400 mb-3">Actions rapides</p>
              <div className="flex flex-wrap gap-2">
                {selectedEvent.type === 'absence' && (
                  <Button 
                    onClick={() => { toggleStatus('absence', selectedEvent, 'is_justified'); setShowEventDetailsModal(false); }} 
                    size="sm" 
                    className={selectedEvent.is_justified ? 'bg-slate-600 hover:bg-slate-500' : 'bg-emerald-600 hover:bg-emerald-700'}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    {selectedEvent.is_justified ? 'Retirer justification' : 'Marquer justifié'}
                  </Button>
                )}
                {selectedEvent.type === 'overtime' && (
                  <>
                    <Button 
                      onClick={() => { toggleStatus('overtime', selectedEvent, 'is_validated'); setShowEventDetailsModal(false); }} 
                      size="sm" 
                      className={selectedEvent.is_validated ? 'bg-slate-600 hover:bg-slate-500' : 'bg-purple-600 hover:bg-purple-700'}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      {selectedEvent.is_validated ? 'Invalider' : 'Valider'}
                    </Button>
                    <Button 
                      onClick={() => { toggleStatus('overtime', selectedEvent, 'is_paid'); setShowEventDetailsModal(false); }} 
                      size="sm" 
                      className={selectedEvent.is_paid ? 'bg-slate-600 hover:bg-slate-500' : 'bg-emerald-600 hover:bg-emerald-700'}
                    >
                      <DollarSign className="w-4 h-4 mr-1" />
                      {selectedEvent.is_paid ? 'Non payé' : 'Marquer payé'}
                    </Button>
                  </>
                )}
                {selectedEvent.type === 'recovery' && (
                  <Button 
                    onClick={() => { toggleStatus('recovery', selectedEvent, 'is_taken'); setShowEventDetailsModal(false); }} 
                    size="sm" 
                    className={selectedEvent.is_taken ? 'bg-slate-600 hover:bg-slate-500' : 'bg-emerald-600 hover:bg-emerald-700'}
                  >
                    <Coffee className="w-4 h-4 mr-1" />
                    {selectedEvent.is_taken ? 'Non prise' : 'Marquer prise'}
                  </Button>
                )}
                
                <Button 
                  onClick={() => setShowConvertModal(true)} 
                  size="sm" 
                  variant="outline" 
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                >
                  <Repeat className="w-4 h-4 mr-1" />
                  Convertir
                </Button>
                
                <Button 
                  onClick={() => deleteEvent(selectedEvent.type, selectedEvent.id)} 
                  size="sm" 
                  variant="outline" 
                  className="border-red-500 text-red-400 hover:bg-red-500/20"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Convert Event */}
      {showConvertModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-blue-500/50 max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Repeat className="w-5 h-5 text-blue-400" />
                Convertir l'événement
              </h2>
              <Button onClick={() => setShowConvertModal(false)} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="bg-slate-900/50 rounded-xl p-4 mb-4">
              <p className="text-sm text-slate-400">Événement actuel</p>
              <p className="text-white font-medium">
                {selectedEvent.type === 'absence' ? 'Absence' : selectedEvent.type === 'overtime' ? 'Heures sup' : 'Récupération'} - {selectedEvent.hours}h
              </p>
              <p className="text-sm text-slate-400">{selectedEvent.employee_name} • {new Date(selectedEvent.date).toLocaleDateString('fr-FR')}</p>
            </div>
            
            <p className="text-slate-300 mb-4">Convertir en :</p>
            
            <div className="space-y-2">
              {selectedEvent.type !== 'absence' && (
                <Button 
                  onClick={() => convertEvent(selectedEvent.type, 'absence')} 
                  className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 justify-start"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Absence (sera justifiée automatiquement)
                </Button>
              )}
              {selectedEvent.type !== 'overtime' && (
                <Button 
                  onClick={() => convertEvent(selectedEvent.type, 'overtime')} 
                  className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 justify-start"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Heures supplémentaires
                </Button>
              )}
              {selectedEvent.type !== 'recovery' && (
                <Button 
                  onClick={() => convertEvent(selectedEvent.type, 'recovery')} 
                  className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 justify-start"
                >
                  <Coffee className="w-4 h-4 mr-2" />
                  Récupération
                </Button>
              )}
            </div>
            
            <Button onClick={() => setShowConvertModal(false)} variant="outline" className="w-full mt-4 border-slate-600 text-slate-300">
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
